import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  deletePartnerRequest,
  getPartnerRequests,
  setPartnerRequestStatus,
} from "../../api/adminService";
import { PartnerRequestStatusLabels, PartnerRequestStatusColors, formatDate } from "./constants";

export default function PartnerRequests() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [saving, setSaving] = useState(false);

  function loadRequests() {
    setLoading(true);
    getPartnerRequests({
      status: statusFilter || undefined,
      search: search || undefined,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load partner requests. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, paginationModel]);

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPaginationModel((p) => ({ ...p, page: 0 }));
    loadRequests();
  }

  function openDetail(row) {
    setSelected(row);
    setNoteDraft(row.adminNote || "");
  }

  async function handleStatusChange(row, status) {
    const updated = await setPartnerRequestStatus(row.partnerRequestId, status, row.adminNote);
    setRows((prev) =>
      prev.map((r) => (r.partnerRequestId === row.partnerRequestId ? { ...r, ...updated } : r))
    );
    if (selected?.partnerRequestId === row.partnerRequestId) {
      setSelected((prev) => ({ ...prev, ...updated }));
    }
  }

  async function handleSaveNote() {
    if (!selected) return;
    setSaving(true);
    try {
      const updated = await setPartnerRequestStatus(
        selected.partnerRequestId,
        selected.status,
        noteDraft
      );
      setRows((prev) =>
        prev.map((r) => (r.partnerRequestId === selected.partnerRequestId ? { ...r, ...updated } : r))
      );
      setSelected((prev) => ({ ...prev, ...updated }));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(row) {
    await deletePartnerRequest(row.partnerRequestId);
    setRows((prev) => prev.filter((r) => r.partnerRequestId !== row.partnerRequestId));
    setRowCount((prev) => prev - 1);
    if (selected?.partnerRequestId === row.partnerRequestId) {
      setSelected(null);
    }
  }

  const columns = useMemo(
    () => [
      { field: "fullName", headerName: "Name", flex: 1, minWidth: 160 },
      { field: "phone", headerName: "Phone", width: 140 },
      {
        field: "businessName",
        headerName: "Business",
        flex: 1,
        minWidth: 160,
        valueGetter: (v) => v || "-",
      },
      { field: "city", headerName: "City", width: 130, valueGetter: (v) => v || "-" },
      {
        field: "status",
        headerName: "Status",
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={PartnerRequestStatusLabels[params.value] || params.value}
            color={PartnerRequestStatusColors[params.value] || "default"}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: "Received",
        width: 170,
        valueFormatter: (v) => formatDate(v),
      },
    ],
    []
  );

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Typography variant="h4">Partner Requests</Typography>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack
        component="form"
        onSubmit={handleSearchSubmit}
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <TextField
          label="Search name, phone, business or email"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <MenuItem value="">All statuses</MenuItem>
            {Object.entries(PartnerRequestStatusLabels).map(([value, label]) => (
              <MenuItem key={value} value={value}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.partnerRequestId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          onRowClick={(params) => openDetail(params.row)}
          sx={{ border: "none" }}
        />
      </Box>

      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}>
        {selected && (
          <Box sx={{ width: 360, p: 3 }}>
            <Typography variant="h6">{selected.fullName}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Received {formatDate(selected.createdAt)}
            </Typography>

            <Stack spacing={1.5} sx={{ mb: 3 }}>
              <DetailRow label="Phone" value={selected.phone} />
              <DetailRow label="Email" value={selected.email || "-"} />
              <DetailRow label="Business Name" value={selected.businessName || "-"} />
              <DetailRow label="City" value={selected.city || "-"} />
            </Stack>

            {selected.message && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Message
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  {selected.message}
                </Typography>
              </>
            )}

            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={selected.status}
                onChange={(e) => handleStatusChange(selected, e.target.value)}
              >
                {Object.entries(PartnerRequestStatusLabels).map(([value, label]) => (
                  <MenuItem key={value} value={Number(value)}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Admin Note"
              value={noteDraft}
              onChange={(e) => setNoteDraft(e.target.value)}
              multiline
              minRows={3}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" onClick={handleSaveNote} disabled={saving} fullWidth>
                Save Note
              </Button>
              <Button color="error" onClick={() => handleDelete(selected)} fullWidth>
                Delete
              </Button>
            </Stack>
          </Box>
        )}
      </Drawer>
    </Box>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600}>
        {value}
      </Typography>
    </Stack>
  );
}
